import { repairImageKitUrl } from '@dupe/common/constants'
import { useState } from 'react'
import { ImageOff, Star, X } from 'lucide-react'
import { cn } from '~/src/lib/utils'

interface SeoReportSkipThisOneProps {
  name: string
  image?: string | null
  summary: string
  rating?: { score: number; count: string }
  reasons: string[]
}

export function SeoReportSkipThisOne({
  name,
  image,
  summary,
  rating,
  reasons,
}: SeoReportSkipThisOneProps) {
  const [imageError, setImageError] = useState(false)
  const showImage = image && !imageError

  return (
    <section className='flex flex-col gap-6'>
      <div className='flex items-start justify-between'>
        <h2 className='font-display text-2xl font-bold text-foreground'>
          Skip this one
        </h2>
        <span className='rounded-full bg-[#f05d38] px-3 py-1 text-sm font-medium leading-6 text-white'>
          {/* color-token-exception: skip-section badge; no semantic token for this orange-red */}
          Not worth it
        </span>
      </div>

      <div
        className='flex flex-col gap-4 rounded-3xl p-4'
        style={{
          /* color-token-exception: skip-section gradient; custom warm-orange surface with no semantic token */
          background:
            'linear-gradient(180deg, rgba(227, 89, 25, 0.07) 0%, rgba(225, 79, 11, 0) 50.14%), #fef3ee',
        }}
      >
        {/* Product info row */}
        <div className='flex items-start gap-3'>
          {showImage ? (
            <div className='h-[120px] w-[120px] shrink-0 overflow-hidden rounded-2xl border border-white/50 bg-white'>
              <img
                src={repairImageKitUrl(image)}
                alt={name}
                className='h-full w-full object-contain p-2'
                loading='lazy'
                onError={() => setImageError(true)}
              />
            </div>
          ) : image ? (
            <div className='flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-2xl border border-white/50 bg-white'>
              <ImageOff
                className='size-8 text-foreground-muted'
                strokeWidth={1.5}
              />
            </div>
          ) : null}
          <div
            className={cn(
              'flex flex-1 flex-col gap-2',
              !showImage && !image && 'w-full'
            )}
          >
            <p className='text-sm font-semibold leading-6 text-foreground-secondary md:text-base'>
              {name}
            </p>
            <p className='text-sm leading-5 text-foreground-secondary'>
              {summary}
            </p>
            {rating && (
              <div className='flex items-center gap-1.5'>
                <div className='flex items-center gap-0.5'>
                  <Star
                    className='size-4 text-yellow-500'
                    fill='currentColor'
                    strokeWidth={0}
                  />
                  <span className='text-sm font-semibold leading-5 text-foreground-secondary'>
                    {rating.score}
                  </span>
                </div>
                <span className='text-sm leading-5 text-foreground-muted'>
                  ({rating.count})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Reasons list */}
        {reasons.length > 0 && (
          <ul className='flex flex-col items-start gap-2.5'>
            {reasons.map((reason) => (
              <li
                key={reason}
                className='inline-flex w-full items-start gap-1 rounded-xl bg-[#fde4d7] py-1 pl-2 pr-2.5 md:w-fit' /* color-token-exception: skip-section error-100 chip bg; no semantic token */
              >
                <span className='flex h-5 shrink-0 items-center'>
                  <X className='size-4 text-[#ed4222]' />
                  {/* color-token-exception: skip-section orange-red; no semantic token */}
                </span>
                <span className='text-balance text-sm font-medium leading-5 text-[#ed4222]'>
                  {/* color-token-exception: skip-section orange-red; no semantic token */}
                  {reason}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
